// Site zones (CR_20260914). Managers create / rename / archive zones through RLS-guarded table writes (no delete:
// archive only); anyone who can act at the site moves assets through the idempotent set_asset_zone RPC. The
// assets trigger keeps the zone tied to custody and writes the asset_zone_moves history.
import type { SiteZone, ZoneMove } from '@fleetspoke/core/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export type { SiteZone, ZoneMove } from '@fleetspoke/core/types';

export interface SetZoneResult {
  moved: number;
  unchanged: number;
  skipped: { asset_id: string; reason: 'not_found' | 'not_active' | 'custody_elsewhere' }[];
}

async function data<T>(
  q: PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>,
): Promise<T> {
  const { data: d, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return d as T;
}

export const zoneKeys = {
  site: (siteId: string) => ['zones', siteId] as const,
  moves: (siteId: string | undefined, assetId: string | undefined) =>
    ['zone-moves', siteId ?? '', assetId ?? ''] as const,
};

/** Every zone of the site, archived included (history and the Zones dialog need them), by name. */
export function useZones(siteId: string | null | undefined) {
  return useQuery({
    queryKey: zoneKeys.site(siteId ?? ''),
    enabled: !!siteId,
    queryFn: () =>
      data<SiteZone[]>(
        supabase()
          .from('site_zones')
          .select('id, company_id, site_id, name, note, is_active')
          .eq('site_id', siteId!)
          .order('name'),
      ),
  });
}

export function activeZones(zones: SiteZone[] | undefined): SiteZone[] {
  return (zones ?? []).filter((z) => z.is_active);
}

// Two FKs point at site_zones (from / to), so each embed names its FK.
const MOVE_COLUMNS =
  'id, asset_id, site_id, reason, at, assets(asset_code), ' +
  'from_zone:site_zones!asset_zone_moves_from_zone_id_fkey(name), ' +
  'to_zone:site_zones!asset_zone_moves_to_zone_id_fkey(name)';

/** Zone moves at a site (site board) or of one asset (its timeline), newest first. */
export function useZoneMoves(siteId?: string, assetId?: string, limit = 50) {
  return useQuery({
    queryKey: zoneKeys.moves(siteId, assetId),
    enabled: !!(siteId || assetId),
    queryFn: () => {
      let q = supabase()
        .from('asset_zone_moves')
        .select(MOVE_COLUMNS)
        .order('at', { ascending: false })
        .limit(limit);
      if (siteId) q = q.eq('site_id', siteId);
      if (assetId) q = q.eq('asset_id', assetId);
      return data<ZoneMove[]>(q);
    },
  });
}

/** Everything that shows a zone: boards, asset pages, lists, the Zones dialog, site counts. */
function useInvalidateZones() {
  const queryClient = useQueryClient();
  return () => {
    for (const key of [['zones'], ['zone-moves'], ['board'], ['assets'], ['asset'], ['sites']]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };
}

/** Add (no id) or rename / re-note a zone. A duplicate active name comes back as code 23505. */
export function useSaveZone() {
  const invalidate = useInvalidateZones();
  return useMutation({
    mutationFn: async (z: {
      id?: string;
      companyId: string;
      siteId: string;
      name: string;
      note: string;
    }) => {
      const row = { name: z.name.trim(), note: z.note.trim() || null };
      await data(
        z.id
          ? supabase().from('site_zones').update(row).eq('id', z.id)
          : supabase()
              .from('site_zones')
              .insert({ ...row, company_id: z.companyId, site_id: z.siteId }),
      );
    },
    onSuccess: invalidate,
  });
}

/** Archive (its assets show "No zone") or restore. */
export function useSetZoneActive() {
  const invalidate = useInvalidateZones();
  return useMutation({
    mutationFn: (a: { id: string; active: boolean }) =>
      data(supabase().from('site_zones').update({ is_active: a.active }).eq('id', a.id)),
    onSuccess: invalidate,
  });
}

/** Put assets into a zone of the site, or take them out (zoneId null). Custody elsewhere → skipped, not refused. */
export function useSetAssetZone() {
  const invalidate = useInvalidateZones();
  return useMutation({
    mutationFn: (a: { siteId: string; assetIds: string[]; zoneId: string | null }) =>
      data<SetZoneResult>(
        supabase().rpc('set_asset_zone', {
          p_idempotency_key: crypto.randomUUID(),
          p_site_id: a.siteId,
          p_asset_ids: a.assetIds,
          p_zone_id: a.zoneId,
        }),
      ),
    onSuccess: invalidate,
  });
}
